// ============================================================
// MovementSection - how far it moves and how big it is.
//
// Movement is derived from Agility whenever Agility is edited (see
// sheetPatches), and may be set by hand afterwards: a jump pack is not
// an Agility score.
// ============================================================
import type { Character } from '../../engine/character/types';
import { SIZE_MODS } from '../../engine/constants';
import { PACES, movementFor } from '../../engine/character/sheetPatches';
import { FIGURE_KINDS, FIGURE_LABELS, readFigure } from '../../engine/figureKind';
import { isObject, resolveBodyShape } from '../../engine/bodyShape';
import ReadoutNumber from './ReadoutNumber';
import styles from './Sheet.module.css';

interface Props {
  character: Character;
  onChange: (partial: Partial<Character>) => void;
  disabled: boolean;
}

/** The sheet's movement, or what its Agility gives when it carries none. */
export function movementOf(character: Character): Character['movement'] {
  return character.movement ?? movementFor(character.characteristics.agi.value);
}

/** "5 / 10 / 15 / 30" for a folded section. */
export function movementSummary(character: Character): string {
  const m = movementOf(character);
  return `${PACES.map((p) => m[p]).join(' / ')} m · ${character.size}`;
}

const signed = (n: number) => (n >= 0 ? `+${n}` : String(n));

/** A person's body is drawn by its figure; a vehicle by its kind and the amorphous mass as goo, so they get no pick. */
const drawnAsPerson = (character: Character) => resolveBodyShape(character) === 'humanoid';

/**
 * The body plate figure: Automatic (derived from the traits) or one the GM pins - the only way to the Robot and the
 * Daemon. Automatic sends undefined: the sheet is stored as text, so the key simply drops (as livery's unpaint).
 */
function FigurePick({ character, onChange, disabled }: Props) {
  return (
    <label className={styles.readout}>
      <span className={styles.readoutLabel}>Figure</span>
      <select
        className={styles.select}
        data-testid="sheet-figure"
        value={readFigure(character.figure) ?? ''}
        disabled={disabled}
        onChange={(e) => onChange({ figure: readFigure(e.target.value) ?? undefined })}
      >
        <option value="">Automatic</option>
        {FIGURE_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {FIGURE_LABELS[kind]}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function MovementSection({ character, onChange, disabled }: Props) {
  const movement = movementOf(character);
  // An Object never moves (2026-10-26): only its Size and token are its own.
  const paces = isObject(character) ? [] : PACES;
  return (
    <div className={styles.factRow}>
      {paces.map((pace) => (
        <ReadoutNumber
          key={pace}
          label={pace}
          ariaLabel={`${pace} move in metres`}
          min={0}
          value={movement[pace]}
          disabled={disabled}
          onChange={(value) => onChange({ movement: { ...movement, [pace]: value } })}
        />
      ))}
      <label className={styles.readout}>
        <span className={styles.readoutLabel}>Size</span>
        <select
          className={styles.select}
          value={character.size}
          disabled={disabled}
          onChange={(e) => onChange({ size: e.target.value })}
        >
          {Object.keys(SIZE_MODS).map((size) => (
            <option key={size} value={size}>
              {size} ({signed(SIZE_MODS[size])})
            </option>
          ))}
        </select>
      </label>
      <ReadoutNumber
        label="Token (m)"
        ariaLabel="Token size in metres"
        title="Drawn marker diameter in metres. 0 sizes it from Size. Visual only - blasts, hordes and reach are unaffected."
        min={0}
        step={0.5}
        float
        value={character.tokenDiameter ?? 0}
        disabled={disabled}
        onChange={(value) => onChange({ tokenDiameter: value > 0 ? value : 0 })}
      />
      {drawnAsPerson(character) && <FigurePick character={character} onChange={onChange} disabled={disabled} />}
    </div>
  );
}
